import {
  Command,
  Plugin,
  DocumentSelection,
  Element,
  ButtonView,
} from "ckeditor5";

import { IconResetListLine } from "@examspark/common-ui/dist/components/icons";

import ListWalker from "../_nonpublic/list/listwalker";

export const COMMAND_RESET_LIST_STYLES = "resetListStyles";
export const UI_BUTTON_RESET_LIST_STYLES = "resetListStyles";

const getSelectionAffectedList = (selection: DocumentSelection) => {
  const listElement = selection.getSelectedElement() ?? undefined;

  if (listElement != null && listElement.hasAttribute("listItemId")) {
    return listElement;
  }

  return selection
    .getFirstPosition()
    ?.getAncestors()
    .find((e): e is Element => e.is("element") && e.hasAttribute("listItemId"));
};

const getSelectionAffectedListStyles = (selection: DocumentSelection) => {
  const results: Array<{
    element: Element;
    attribute: string;
    getNewValue: () => unknown;
  }> = [];

  const rootListElement = getSelectionAffectedList(selection);
  if (rootListElement == null) return results;

  const listElements = Array.from(
    new ListWalker(rootListElement, {
      direction: "forward",
      includeSelf: true,
      higherIndent: true,
      sameIndent: true,
    }),
  );

  for (const listElement of listElements) {
    if (
      listElement.hasAttribute("listStyle") &&
      listElement.getAttribute("listStyle") !== "default"
    ) {
      results.push({
        element: listElement,
        attribute: "listStyle",
        getNewValue: () => "default",
      });
    }

    const htmlAttributes = listElement.getAttribute("htmlLiAttributes") as
      | HtmlAttributes
      | undefined;
    if (htmlAttributes?.styles?.["list-style-type"] != null) {
      results.push({
        element: listElement,
        attribute: "htmlLiAttributes",
        getNewValue: () => {
          delete htmlAttributes?.styles?.["list-style-type"];
          if (Object.keys(htmlAttributes?.styles ?? {}).length === 0) {
            delete htmlAttributes.styles;
          }
          return htmlAttributes;
        },
      });
    }
  }

  return results;
};

type HtmlAttributes = Record<string, any> & { styles?: Record<string, string> };

export class ResetListStylesCommand extends Command {
  override refresh() {
    const results = getSelectionAffectedListStyles(
      this.editor.model.document.selection,
    );
    this.isEnabled = results.length > 0;
  }

  override execute() {
    const results = getSelectionAffectedListStyles(
      this.editor.model.document.selection,
    );
    if (results.length === 0) return;

    this.editor.model.change((writer) => {
      for (const { element, attribute, getNewValue } of results) {
        writer.setAttribute(attribute, getNewValue(), element);

        if (attribute.startsWith("html")) {
          //
          // GHS attributes aren't picked up by the list plugin, so we need to
          // tell it to reconvert
          //
          this.editor.editing.reconvertItem(element);
        }
      }
    });
  }
}

export default class ResetListStyles extends Plugin {
  init() {
    const command = new ResetListStylesCommand(this.editor);
    this.editor.commands.add(COMMAND_RESET_LIST_STYLES, command);

    this.editor.ui.componentFactory.add(
      UI_BUTTON_RESET_LIST_STYLES,
      (locale) => {
        const button = new ButtonView(locale);

        button.set({
          tooltip: this.editor.t("Reset list styles"),
          icon: IconResetListLine.Content,
        });

        button.bind("isEnabled").to(command, "isEnabled");

        button.on("execute", () => command.execute());

        return button;
      },
    );
  }
}
